import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { ReactComponent as UploadIcon } from "../../../../images/uploadIcon.svg";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const FilesUploadPopup = ({
  title = "Upload Files",
  description = "Choose files to upload: DOC, Excel, PDF, Audio, Video",
  accept = ".docx,.pdf,.png,.jpeg,.jpg,.mp4,.mp3,.mov,.avi,.mkv,.eml,.msg,.mbox",
  onClose,
  onFileUpload,
  useNavigateBack = false,
  onBack,
}) => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleBackClick = () => {
    if (onBack) {
      navigate(onBack);
    } else {
      if (onClose) onClose();
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    // Define allowed formats based on the accept prop or default
    const allowedFormats = accept
      .split(",")
      .map((ext) => ext.trim().toLowerCase());

    const invalidFiles = files.filter((file) => {
      const fileExtension = "." + file.name.split(".").pop().toLowerCase();
      return !allowedFormats.includes(fileExtension);
    });

    if (invalidFiles.length > 0) {
      setSnackbarMessage(
        `Unsupported file format(s). Please upload only ${accept} files.`
      );
      setSnackbarOpen(true);
      return;
    }

    setUploadedFiles((prev) => [...prev, ...files]);

    if (onFileUpload) onFileUpload(files);

    if (onClose) onClose();
  };

  return (
    <Box sx={{ maxWidth: "580px", width: "100%", py: 1, ml: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
        <IconButton
          onClick={onBack}
          edge="start"
          aria-label="back"
          sx={{ color: "black" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            fontSize: { xs: "16px", md: "18px" },
          }}
        >
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2 }}>
        <Typography
          sx={{ mb: 2, fontWeight: 500, fontSize: { xs: "14px", md: "15px" } }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            border: "1.5px dashed #1890FF",
            borderRadius: "8px",
            backgroundColor: "#E6F7FF",
            p: 8,
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#0b0c0cff",
              fontWeight: 600,
              mb: 2,
              fontSize: { xs: "13px", md: "14px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <UploadIcon sx={{ fill: "#0e0f0fff" }} />
            Upload files
          </Typography>

          <Typography
            sx={{
              color: "#8c8c8c",
              mb: 3,
              fontSize: { xs: "13px", md: "14px" },
            }}
          >
            Drag and drop your files here or click to choose and select files
          </Typography>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#3898fc",
              borderRadius: "6px",
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "14px", md: "15px" },
              px: 3,
              py: 1,
              "&:hover": { backgroundColor: "#2e7fc4" },
              width: { xs: "100%", md: "auto" },
            }}
            startIcon={<UploadIcon sx={{ fill: "white" }} />}
            component="label"
          >
            Choose File
            <input
              type="file"
              hidden
              multiple
              accept={accept}
              onChange={handleFileChange}
            />
          </Button>
        </Box>

        {uploadedFiles.length > 0 && (
          <List sx={{ mt: 3 }}>
            {uploadedFiles.map((file, idx) => (
              <ListItem
                key={idx}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  mb: 1,
                  background: "#fafafa",
                }}
              >
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(2)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000} //4 seconds
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FilesUploadPopup;
